import { Klydo } from "../../../api/klydo";

/**
 * Serialises a list of klydos to the Android SharedPreferences cache format used
 * by `cache_klydos.xml` — a <map> of <string name="<klydoId>">…</string> entries
 * whose value is the klydo JSON (HTML-escaped).
 *
 * Field mapping (backoffice Klydo -> cache JSON), matching how the clock app
 * builds its own cache:
 *   - theme.{back,pendulum,rod,hands}  <- theme colors as signed 32-bit ARGB
 *   - mainLoop                         <- loopUrl basename without extension
 *   - creator / name / created / updated
 *   - tag13 / tagPSF                   <- tags contains "13+" / "PSF"
 *   - favorites / viewsCount / time    <- stats.{favorites,viewed,time}
 *
 * A handful of device-only fields have no backoffice source, so they default to
 * the values seen in the reference cache. Note the reference is a firmware SEED
 * cache: every entry has favorites/viewsCount/time = 0 and viewed = false (the
 * device fills real counters at runtime), so those are emitted as zero here too
 * rather than the live stats.
 */

const DEFAULTS = {
  zipL: true,
  zipR: true,
  oem: true,
  viewed: false,
  favorites: 0,
  viewsCount: 0,
  time: 0,
  margins: [0, 0, 0, 0] as number[],
};

// "#RRGGBB" / "#AARRGGBB" (or without the leading #) -> signed 32-bit ARGB int,
// e.g. "#000000" -> 0xFF000000 -> -16777216. Missing alpha is treated as opaque.
const colorToSignedInt = (color: string | undefined): number => {
  if (!color) return 0;
  let hex = color.trim().replace(/^#/, "");
  if (hex.length === 6) hex = `FF${hex}`; // add full alpha
  if (hex.length === 3) {
    // #RGB shorthand -> #RRGGBB -> add alpha
    hex = `FF${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }
  const value = parseInt(hex, 16);
  if (Number.isNaN(value)) return 0;
  // `| 0` reinterprets the unsigned ARGB as a signed 32-bit int.
  return value | 0;
};

// Mirror klydo-api's derivation: loopUrl.split('/').pop().split('.')[0].
const loopUrlToMainLoop = (loopUrl: string | undefined): string => {
  if (!loopUrl) return "";
  const fileName = loopUrl.split("/").pop() ?? "";
  return fileName.split("?")[0].split(".")[0];
};

const toEpochMs = (date: Date | string | undefined): number => {
  if (!date) return 0;
  const ms = new Date(date).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

const escapeXml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const klydoToCacheObject = (k: Klydo) => ({
  id: k.id,
  theme: {
    back: colorToSignedInt(k.theme?.backgroundColor),
    pendulum: colorToSignedInt(k.theme?.pendulumColor),
    rod: colorToSignedInt(k.theme?.pendulumRodColor),
    hands: colorToSignedInt(k.theme?.handsColor),
  },
  mainLoop: loopUrlToMainLoop(k.loopUrl),
  creator: k.creator ?? "",
  created: toEpochMs(k.createdAt),
  updated: toEpochMs(k.updatedAt),
  name: k.name ?? "",
  zipL: DEFAULTS.zipL,
  zipR: DEFAULTS.zipR,
  tag13: k.tags?.includes("13+") ?? false,
  tagPSF: k.tags?.includes("PSF") ?? false,
  viewed: DEFAULTS.viewed,
  favorites: DEFAULTS.favorites,
  viewsCount: DEFAULTS.viewsCount,
  time: DEFAULTS.time,
  oem: DEFAULTS.oem,
  margins: DEFAULTS.margins,
});

export const buildFirmwareKlydosXml = (klydos: Klydo[]): string => {
  const entries = klydos
    .map((k) => {
      const json = JSON.stringify(klydoToCacheObject(k));
      return `    <string name="${escapeXml(k.id)}">${escapeXml(json)}</string>`;
    })
    .join("\n");
  return `<?xml version='1.0' encoding='utf-8' standalone='yes' ?>\n<map>\n${entries}\n</map>`;
};

export const downloadXml = (fileName: string, xml: string): void => {
  const blob = new Blob([xml], { type: "application/xml" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
